/**
 * Placeholder audit-intelligence data. Replace with the analysis API; each
 * export corresponds to one panel of the report.
 */

export const LAST_ANALYSIS = "01 Sep 2026, 10:30 AM";

/**
 * Four severity bands. High/Medium/Low keep the app-wide risk colours; "Very
 * Low" takes a cool hue rather than a second green — orange and yellow (the
 * mockup's Low) are not separable for red-green colour-vision deficiency.
 */
export const BANDS = [
  { key: "high", label: "High Risk", color: "#df3f45" },
  { key: "medium", label: "Medium Risk", color: "#ef9c17" },
  { key: "low", label: "Low Risk", color: "#26a269" },
  { key: "veryLow", label: "Very Low Risk", color: "#1b6fd6" },
];

export const AUDIT_STATS = [
  {
    key: "score",
    label: "Overall Audit Risk Score",
    value: "72",
    unit: "/100",
    sub: "High Risk",
    subTone: "critical",
    tone: "green",
    icon: "shield",
  },
  {
    key: "highrisk",
    label: "High Risk Taxpayers",
    value: "1,248",
    delta: "14.2%",
    deltaNote: "vs last analysis",
    tone: "red",
    icon: "alert",
  },
  {
    key: "anomalies",
    label: "Anomalies Detected",
    value: "3,846",
    delta: "9.7%",
    deltaNote: "vs last analysis",
    tone: "amber",
    icon: "pulse",
  },
  {
    key: "patterns",
    label: "Patterns Identified",
    value: "78",
    delta: "12.5%",
    deltaNote: "vs last analysis",
    deltaTone: "good",
    tone: "indigo",
    icon: "network",
  },
  {
    key: "points",
    label: "Data Points Analyzed",
    value: "245.6M",
    sub: LAST_ANALYSIS,
    tone: "blue",
    icon: "database",
  },
];

export const KEY_RISK_AREAS = [
  {
    key: "duplicates",
    title: "Duplicate & Related Entities",
    text: "Potential duplicate NIFs and related entities with shared characteristics.",
    level: "High",
    icon: "copy",
    tone: "green",
  },
  {
    key: "income",
    title: "Unreported Income",
    text: "Income indicators detected without corresponding tax reporting.",
    level: "High",
    icon: "trend",
    tone: "amber",
  },
  {
    key: "vat",
    title: "VAT Non-Compliance",
    text: "Inconsistencies in VAT declarations and payment patterns.",
    level: "Medium",
    icon: "doc",
    tone: "indigo",
  },
  {
    key: "transfer",
    title: "Transfer Pricing",
    text: "Transactions that deviate from arm's length pricing benchmarks.",
    level: "Medium",
    icon: "transfer",
    tone: "blue",
  },
];

export const TOTAL_FINDINGS = 25;

export const AI_FINDINGS = [
  {
    id: 1,
    name: "Potential Duplicate Entities",
    icon: "copy",
    level: "High",
    affected: 562,
    confidence: 96,
    detected: "01 Sep 2026",
    status: "New",
  },
  {
    id: 2,
    name: "Unreported Income Indicators",
    icon: "trend",
    level: "High",
    affected: 318,
    confidence: 93,
    detected: "31 Aug 2026",
    status: "In Review",
  },
  {
    id: 3,
    name: "VAT Under-Declaration",
    icon: "doc",
    level: "High",
    affected: 274,
    confidence: 91,
    detected: "01 Sep 2026",
    status: "New",
  },
  {
    id: 4,
    name: "Transfer Pricing Anomalies",
    icon: "transfer",
    level: "Medium",
    affected: 186,
    confidence: 88,
    detected: "30 Aug 2026",
    status: "In Review",
  },
  {
    id: 5,
    name: "Related Party Transactions",
    icon: "link",
    level: "Medium",
    affected: 142,
    confidence: 85,
    detected: "29 Aug 2026",
    status: "New",
  },
];

export const ANALYSIS_DATES = [
  "13 Jul",
  "27 Jul",
  "10 Aug",
  "24 Aug",
  "28 Aug",
  "01 Sep",
];

export const RISK_TREND = [
  {
    key: "high",
    label: "High Risk",
    color: "#df3f45",
    values: [11.2, 11.6, 11.4, 12.1, 12.6, 12.8],
  },
  {
    key: "medium",
    label: "Medium Risk",
    color: "#ef9c17",
    values: [26.4, 27.1, 27.6, 27.2, 28.1, 28.6],
  },
  {
    key: "low",
    label: "Low Risk",
    color: "#26a269",
    values: [36.2, 36.8, 37.1, 37.6, 37.9, 38.1],
  },
  {
    key: "veryLow",
    label: "Very Low Risk",
    color: "#1b6fd6",
    values: [26.2, 24.5, 23.9, 23.1, 21.4, 20.5],
  },
];

export const TREND_MAX = 50;

export const SECTOR_RISK = [
  {
    label: "Retail Trade",
    share: 18.2,
    parts: { high: 34, medium: 26, low: 22, veryLow: 18 },
  },
  {
    label: "Construction",
    share: 15.7,
    parts: { high: 31, medium: 28, low: 24, veryLow: 17 },
  },
  {
    label: "Professional Services",
    share: 12.3,
    parts: { high: 27, medium: 29, low: 26, veryLow: 18 },
  },
  {
    label: "Manufacturing",
    share: 9.8,
    parts: { high: 24, medium: 27, low: 28, veryLow: 21 },
  },
  {
    label: "Transport & Storage",
    share: 7.6,
    parts: { high: 29, medium: 26, low: 25, veryLow: 20 },
  },
];

export const ANALYZED_RECORDS = "245.6M";

export const RISK_DISTRIBUTION = [
  {
    label: "High Risk",
    value: 31.4,
    share: 12.8,
    color: "#df3f45",
    legendPrimary: "12.8%",
    legendSecondary: "(31.4M)",
  },
  {
    label: "Medium Risk",
    value: 70.3,
    share: 28.6,
    color: "#ef9c17",
    legendPrimary: "28.6%",
    legendSecondary: "(70.3M)",
  },
  {
    label: "Low Risk",
    value: 93.6,
    share: 38.1,
    color: "#26a269",
    legendPrimary: "38.1%",
    legendSecondary: "(93.6M)",
  },
  {
    label: "Very Low Risk",
    value: 50.3,
    share: 20.5,
    color: "#1b6fd6",
    legendPrimary: "20.5%",
    legendSecondary: "(50.3M)",
  },
];

export const AI_INSIGHTS = [
  "31.4M records (12.8%) are classified as High Risk, a 14.2% increase from the last analysis.",
  "Duplicate entity patterns increased by 18% compared to the previous analysis.",
  "Unreported income indicators are concentrated in Retail Trade, Construction, and Services sectors.",
  "87% of high-risk taxpayers have inconsistencies across multiple data sources.",
];

export const RECOMMENDED_ACTIONS = [
  {
    title: "Review 562 potential duplicate entities",
    text: "High confidence duplicate matches detected.",
  },
  {
    title: "Investigate 318 unreported income cases",
    text: "Significant income indicators without tax reporting.",
  },
  {
    title: "Prioritize VAT non-compliance cases",
    text: "274 taxpayers with VAT declaration inconsistencies.",
  },
];

export const QUICK_ACTIONS = [
  { key: "run", label: "Run New Analysis", icon: "sparkle" },
  { key: "compare", label: "Compare Analyses", icon: "bars" },
  { key: "export", label: "Export Findings", icon: "download" },
  { key: "schedule", label: "Schedule Analysis", icon: "calendar" },
];
